import React, { createContext, useEffect, useState } from 'react';
import { io } from 'socket.io-client';
import { useAwarenessContext, useChuckClientContext, useLocalAppointmentCreation, useLocalAppointmentUpdate, useLocalConversationCreation, useLocalConversationUpdate, useLocalHuddleDeletion, useLocalHuddleUpdate, useLocalMatchCreation, useLocalMatchDeletion, useLocalMatchUpdate, useLocalMessageCreation, useProfileContext, useUserContext } from '../hooks';
export const SocketContext = createContext(undefined);
export const SocketContextProvider = ({ url, children }) => {
    const { profile } = useProfileContext();
    const { token, user, setUser } = useUserContext();
    const { client } = useChuckClientContext();
    const [connection, setConnection] = useState(null);
    const { update: updateAwareness, focusedConversation } = useAwarenessContext();
    const { mutate: updateLocalMatch } = useLocalMatchUpdate();
    const { mutate: createLocalMatch } = useLocalMatchCreation();
    const { mutate: deleteLocalMatch } = useLocalMatchDeletion();
    const { mutate: createLocalConversation } = useLocalConversationCreation();
    const { mutate: updateLocalConversation } = useLocalConversationUpdate();
    const { mutate: createLocalMessage } = useLocalMessageCreation();
    const { mutate: createLocalAppointment } = useLocalAppointmentCreation();
    const { mutate: updateLocalAppointment } = useLocalAppointmentUpdate();
    const { mutate: updateLocalHuddle } = useLocalHuddleUpdate();
    const { mutate: deleteLocalHuddle } = useLocalHuddleDeletion();
    useEffect(() => {
        if (!token || !(profile === null || profile === void 0 ? void 0 : profile.id))
            return;
        const connection = io(url, {
            auth: {
                token
            }
        });
        setConnection(connection);
        const onError = (err) => {
            console.error(err);
            client.captureException(err);
            client.captureException('SOCKET_ERROR');
        };
        const onConnectionError = (err) => {
            console.error(err);
            client.captureException(err);
            client.captureException('SOCKET_CONNECTION_ERROR');
        };
        const onReconnectionError = (err) => {
            console.error(err);
            client.captureException(err);
            client.captureException('SOCKET_RECONNECTION_ERROR');
        };
        const onReconnectionFailure = () => {
            client.captureException('SOCKET_RECONNECTION_FAILURE');
        };
        connection.io.on('error', onError);
        connection.io.on('reconnect_error', onReconnectionError);
        connection.io.on('reconnect_failed', onReconnectionFailure);
        connection.on('connect_error', onConnectionError);
        return () => {
            connection.io.off();
            connection.off();
            connection.disconnect();
            setConnection(null);
        };
    }, [token, profile === null || profile === void 0 ? void 0 : profile.id]);
    useEffect(() => {
        if (!connection)
            return;
        const onMessage = (data) => {
            const events = Array.isArray(data) ? data : [data];
            for (const event of events) {
                const { action, payload } = event;
                switch (action) {
                    case 'create:match':
                        createLocalMatch(payload);
                        break;
                    case 'update:match':
                        updateLocalMatch(payload);
                        break;
                    case 'delete:match':
                        deleteLocalMatch(payload);
                        break;
                    case 'create:conversation':
                        createLocalConversation(payload);
                        break;
                    case 'update:conversation':
                        const data = payload;
                        updateLocalConversation({
                            data,
                            isFocused: focusedConversation.id === data.id,
                            visibility: focusedConversation.visibility,
                            user: profile.id
                        });
                        break;
                    case 'create:message':
                        createLocalMessage(payload);
                        break;
                    case 'create:appointment':
                        createLocalAppointment(payload);
                        break;
                    case 'update:appointment':
                        updateLocalAppointment(payload);
                        break;
                    case 'update:awareness':
                        updateAwareness(payload);
                        break;
                    case 'update:user':
                        if (user)
                            setUser(Object.assign(Object.assign({}, user), payload));
                        break;
                    case 'update:huddle':
                        updateLocalHuddle(payload);
                        break;
                    case 'delete:huddle':
                        deleteLocalHuddle(payload);
                        break;
                    default:
                        break;
                }
            }
        };
        connection.on('message', onMessage);
        return () => {
            connection.off('message', onMessage);
        };
    }, [
        connection,
        createLocalConversation,
        updateLocalConversation,
        updateLocalMatch,
        createLocalMatch,
        createLocalMessage,
        focusedConversation,
        deleteLocalMatch,
        profile,
        createLocalAppointment,
        updateLocalAppointment
    ]);
    return (React.createElement(SocketContext.Provider, { value: connection }, children));
};
//# sourceMappingURL=SocketContext.js.map